import React, {
  DetailedHTMLProps,
  InputHTMLAttributes,
  forwardRef,
  useMemo,
} from 'react';
import { useIntl } from 'react-intl';
import classnames from 'classnames';

export type CustomerTextFieldProps = DetailedHTMLProps<
  InputHTMLAttributes<HTMLInputElement>,
  HTMLInputElement
> & {
  labelText: string;
  isBlock?: boolean;
  isError?: boolean;
};

export const CustomerTextField = forwardRef<
  HTMLInputElement,
  CustomerTextFieldProps
>(({ labelText, isBlock, isError, ...props }, ref) => {
  const { disabled, readOnly, required } = props;

  const intl = useIntl();

  const color = '#0066FF';

  const isDisabled = useMemo(() => {
    return disabled || readOnly;
  }, [disabled, readOnly]);

  const bgColorClasses = useMemo(() => {
    if (isDisabled) {
      return ['bg-gray-10'];
    }

    if (isError) {
      return ['bg-red-10'];
    }

    return ['bg-white'];
  }, [isError, isDisabled]);

  return (
    <>
      <label className={!isBlock ? 'inline-block' : 'block w-full'}>
        <div className="mb-0.5">
          <span className="rs-font text-13 text-gray-1000 pc:text-12 font-normal">
            {labelText}
            {required && (
              <span className="text-red-700">
                {intl.formatMessage({
                  defaultMessage: '（必須）',
                  id: 'FLEIzy',
                  description:
                    'src/templates/merchants/resources/book/components/forms/CustomerTextField/index.tsx',
                })}
              </span>
            )}
          </span>
        </div>
        <div
          className={classnames(
            'input-wrapper',
            'box-border',
            !isBlock ? 'inline-block' : 'block w-full',
            'h-48',
            'pc:h-44',
            bgColorClasses,
            'rounded-15',
            isError && !isDisabled ? 'ring-red-700' : 'ring-gray-200',
            'ring-1',
            'transition-all',
            'focus-within:bg-white',
            'focus-within:ring-2',
          )}
        >
          <input
            {...props}
            ref={ref}
            className={classnames(
              'input',
              'rs-font',
              'box-border',
              'w-full',
              'h-full',
              'px-2',
              'text-16',
              'pc:text-15',
              !isDisabled ? 'text-gray-1000' : 'text-gray-400',
              'font-normal',
              'bg-transparent',
              'border-none',
              'outline-none',
              'focus:outline-none',
              'placeholder:text-gray-200',
              !isDisabled ? 'cursor-auto' : 'cursor-not-allowed',
            )}
          />
        </div>
      </label>
      <style jsx>{`
        .input-wrapper:focus-within {
          --tw-ring-color: ${color};
        }
        .input {
          caret-color: ${color};
        }
      `}</style>
    </>
  );
});

CustomerTextField.displayName = 'CustomerTextField';
